import { Moon, Users, Megaphone, AlertTriangle, DoorOpen } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Badge } from '@/components/ui/badge'
import type { FactionStatus } from '@/types'

export interface FactionStatusBadgeProps {
  status: FactionStatus
  size?: 'sm' | 'md' | 'lg'
  showIcon?: boolean
  className?: string
}

const STATUS_CONFIG: Record<
  FactionStatus,
  {
    label: string
    className: string
    icon: React.ComponentType<{ className?: string }>
  }
> = {
  dormant: {
    label: 'Dormant',
    className: 'bg-slate-500/20 text-slate-400 border-slate-500/30',
    icon: Moon,
  },
  active: {
    label: 'Active',
    className: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    icon: Users,
  },
  demanding: {
    label: 'Demanding',
    className: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
    icon: Megaphone,
  },
  threatening: {
    label: 'Threatening',
    className: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
    icon: AlertTriangle,
  },
  defecting: {
    label: 'Defecting',
    className: 'bg-red-500/20 text-red-400 border-red-500/30',
    icon: DoorOpen,
  },
}

const SIZE_CLASSES = {
  sm: 'text-[10px] px-1.5 py-0.5',
  md: 'text-xs px-2.5 py-0.5',
  lg: 'text-sm px-3 py-1',
}

const ICON_SIZES = {
  sm: 'h-2.5 w-2.5',
  md: 'h-3 w-3',
  lg: 'h-4 w-4',
}

export function FactionStatusBadge({
  status,
  size = 'md',
  showIcon = true,
  className,
}: FactionStatusBadgeProps) {
  const config = STATUS_CONFIG[status]
  const Icon = config.icon

  return (
    <Badge
      variant="outline"
      className={cn(
        'gap-1 font-medium',
        config.className,
        SIZE_CLASSES[size],
        className
      )}
    >
      {showIcon && <Icon className={ICON_SIZES[size]} />}
      {config.label}
    </Badge>
  )
}
